import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table2, BarChart3 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltipHost, LabelList, ResponsiveContainer,
} from 'recharts';
import IconButton from '../../../components/ui/IconButton';
import Tooltip from '../../../components/ui/Tooltip';
import DashboardCard from './DashboardCard';
import ChartTooltip from './ChartTooltip';
import {
  STACK_KEYS, segmentRadius, segmentWidth, segmentPath,
} from './chartHelpers';
import {
  Legend, LegendItem, NumTable, Footnote,
} from '../styles';
import { SERIES, CARD_IDS } from '../constants';
import { formatNumber } from '../format';
import { buildDrillLink } from '../../../utils/attendanceFilterQuery';

// Yığılmış dilim → seri (renk ve rapor türü varlığı izler).
const STACK_SERIES = { latePerCapita: 'late', earlyPerCapita: 'early', absentPerCapita: 'absent' };
const COUNT_FIELD = { late: 'late', early: 'early', absent: 'absent' };
const SERIES_LABEL = {
  late: 'attendanceDashboard.series.late',
  early: 'attendanceDashboard.series.early',
  absent: 'attendanceDashboard.series.absent',
};

function DepartmentTooltip({
  active, payload, t, lang,
}) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <ChartTooltip
      title={row.name}
      rows={STACK_KEYS.map((key) => {
        const kind = STACK_SERIES[key];
        return {
          key,
          color: SERIES[kind],
          kind: 'box',
          value: t('attendanceDashboard.departments.tooltip', {
            value: formatNumber(row[key], lang, 2),
            count: row[COUNT_FIELD[kind]] ?? 0,
            headcount: row.headcount ?? 0,
          }),
          label: t(SERIES_LABEL[kind]),
        };
      })}
    />
  );
}

// Departmana Göre: kişi başı olay, yığılmış yatay çubuk (yükseklik satır sayısıyla — iç kaydırma yok). Dilime
// tıklayınca o departmanın o serideki listesi (departmansız satır tıklanmaz).
export default function DepartmentBreakdownCard({
  slice, query, effectiveTo, closedTo, drillable = true,
}) {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const lang = i18n.language;
  const [showTable, setShowTable] = useState(false);

  const items = useMemo(() => slice.data?.items || [], [slice.data]);
  const hidden = slice.data?.hiddenCount || 0;
  const rows = useMemo(() => items.map((item) => ({
    ...item,
    name: item.departmentName || t('attendanceDashboard.departments.unassigned'),
  })), [items, t]);

  // Devamsızlık dünde biter (bugün henüz bitmedi) — liste aynı aralıkla açılır. Geçmiş günü olmayan dönemde bağlantı yok.
  const canDrill = Boolean(query && drillable);
  const drill = (row, kind) => {
    if (!row?.departmentId || !canDrill) return;
    const to = kind === 'absent' ? (closedTo || query.to) : (effectiveTo || query.to);
    history.push(buildDrillLink(kind, query, { departmentId: row.departmentId, to }));
  };

  const shapeFor = (key) => function Segment(props) {
    const {
      x, y, width, height, payload,
    } = props;
    if (!width || width <= 0 || !height) return null;
    const radius = segmentRadius(payload, key);
    const isLast = radius[1] > 0;
    const w = segmentWidth(width, isLast);
    if (w <= 0) return null;
    return (
      <path
        d={segmentPath(x, y, w, height, radius)}
        fill={SERIES[STACK_SERIES[key]]}
        style={{ cursor: payload?.departmentId && canDrill ? 'pointer' : 'default' }}
        onClick={() => drill(payload, STACK_SERIES[key])}
      />
    );
  };

  const toggle = (
    <span data-export-exclude>
      <Tooltip text={showTable ? t('attendanceDashboard.table.hide') : t('attendanceDashboard.table.show')}>
        <IconButton
          type="button"
          size="sm"
          data-action="toggle-table"
          aria-label={showTable ? t('attendanceDashboard.table.hide') : t('attendanceDashboard.table.show')}
          onClick={() => setShowTable((v) => !v)}
        >
          {showTable ? <BarChart3 size={14} /> : <Table2 size={14} />}
        </IconButton>
      </Tooltip>
    </span>
  );

  return (
    <DashboardCard
      cardKey="departmana-gore"
      title={t('attendanceDashboard.departments.title')}
      meta={t('attendanceDashboard.departments.meta')}
      span={4}
      spanMd={6}
      slice={slice}
      empty={rows.length === 0}
      domId={CARD_IDS.departments}
      headerExtra={toggle}
    >
      <Legend style={{ marginTop: 0, marginBottom: 8 }}>
        {STACK_KEYS.map((key) => (
          <LegendItem key={key} $color={SERIES[STACK_SERIES[key]]}>{t(SERIES_LABEL[STACK_SERIES[key]])}</LegendItem>
        ))}
      </Legend>
      {showTable ? (
        <NumTable data-view="table">
          <thead>
            <tr>
              <th>{t('attendanceDashboard.departments.department')}</th>
              <th className="n">{t('attendanceDashboard.departments.headcount')}</th>
              <th className="n">{t(SERIES_LABEL.late)}</th>
              <th className="n">{t(SERIES_LABEL.early)}</th>
              <th className="n">{t(SERIES_LABEL.absent)}</th>
              <th className="n">{t('attendanceDashboard.departments.total')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.departmentId || 'unassigned'} data-row={row.departmentId || 'unassigned'}>
                <td>{row.name}</td>
                <td className="n">{formatNumber(row.headcount, lang)}</td>
                <td className="n">{formatNumber(row.late, lang)}</td>
                <td className="n">{formatNumber(row.early, lang)}</td>
                <td className="n">{formatNumber(row.absent, lang)}</td>
                <td className="n">{formatNumber(row.totalPerCapita, lang, 2)}</td>
              </tr>
            ))}
          </tbody>
        </NumTable>
      ) : (
        <div data-view="chart">
          <ResponsiveContainer width="100%" height={rows.length * 32 + 8}>
            <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 40, bottom: 4, left: 0 }}>
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                width={110}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: '#374151' }}
              />
              <ChartTooltipHost cursor={{ fill: 'rgba(2, 145, 239, 0.05)' }} content={<DepartmentTooltip t={t} lang={lang} />} />
              {STACK_KEYS.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="s"
                  barSize={14}
                  shape={shapeFor(key)}
                  isAnimationActive={false}
                >
                  {index === STACK_KEYS.length - 1 ? (
                    <LabelList
                      dataKey="totalPerCapita"
                      position="right"
                      formatter={(value) => formatNumber(value, lang, 2)}
                      style={{ fontSize: 11, fill: '#374151' }}
                    />
                  ) : null}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {hidden > 0 ? <Footnote>{t('attendanceDashboard.departments.more', { count: hidden })}</Footnote> : null}
    </DashboardCard>
  );
}
